import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Card,
  Descriptions,
  Tag,
  Spin,
  Alert,
  Button,
  Space,
  Divider,
  Row,
  Col,
} from 'antd';
import {
  ArrowLeftOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  BankOutlined,
} from '@ant-design/icons';
import { useClientInfo } from '../../features/clients/hooks';

const { Title, Text } = Typography;

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const clientId = id ? parseInt(id, 10) : null;
  const { client, isLoading, error } = useClientInfo(clientId);

  const formatPhone = (phone: string) => {
    if (phone.length === 11) {
      return `${phone[0]}-${phone.slice(1, 4)}-${phone.slice(4, 7)}-${phone.slice(7, 9)}-${phone.slice(9)}`;
    }
    return phone;
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="default" />
        <div style={{ marginTop: 14 }}>
          <Text type="secondary" style={{ fontSize: 13 }}>Загрузка информации о клиенте...</Text>
        </div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div>
        <Button
          size="middle"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/app/search')}
          style={{ marginBottom: 20 }}
        >
          К поиску
        </Button>
        <Alert
          message="Ошибка"
          description={error || 'Клиент не найден'}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div>
      <Button
        size="middle"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/app/search')}
        style={{ marginBottom: 20 }}
      >
        К поиску
      </Button>

      <Row gutter={[20, 20]}>
        <Col xs={24} lg={16}>
          <Card>
            <Space align="start" style={{ marginBottom: 14 }}>
              <UserOutlined style={{ fontSize: 26, color: '#1890ff' }} />
              <div>
                <Title level={4} style={{ margin: 0 }}>{client.name}</Title>
                <Space style={{ marginTop: 6 }}>
                  {client.clientType === 'prepaid' ? (
                    // Show active/blocked status only for prepaid clients
                    client.isBlocked ? (
                      <Tag color="red">Заблокирован</Tag>
                    ) : client.isEnabled ? (
                      <Tag color="green">Активен</Tag>
                    ) : (
                      <Tag color="orange">Отключён</Tag>
                    )
                  ) : (
                    // For postpaid clients, just show "Постоплата" without active status
                    <Tag color="default">Постоплата</Tag>
                  )}
                  <Tag color={client.clientType === 'prepaid' ? 'blue' : 'default'}>
                    {client.clientType === 'prepaid' ? 'PREPAID' : 'POSTPAID'}
                  </Tag>
                  {client.blacklist && <Tag color="error">В чёрном списке</Tag>}
                </Space>
              </div>
            </Space>

            <Divider style={{ margin: '16px 0' }} />

            <Descriptions
              column={{ xs: 1, sm: 2 }}
              labelStyle={{ fontWeight: 500, fontSize: 13 }}
              contentStyle={{ fontSize: 13 }}
              size="small"
            >
              <Descriptions.Item label="Аккаунт">{client.account}</Descriptions.Item>
              <Descriptions.Item label="Телефон">
                <Space>
                  <PhoneOutlined />
                  {formatPhone(client.phone)}
                </Space>
              </Descriptions.Item>
              {client.forwardingNumber && (
                <Descriptions.Item label="Переадресация">
                  {formatPhone(client.forwardingNumber)}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Email">
                {client.email ? (
                  <Space>
                    <MailOutlined />
                    {client.email}
                  </Space>
                ) : (
                  <Text type="secondary">Не указан</Text>
                )}
              </Descriptions.Item>
              {client.additionalEmails.length > 0 && (
                <Descriptions.Item label="Доп. email" span={2}>
                  {client.additionalEmails.join(', ')}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          {client.clientType === 'prepaid' ? (
            <Card title="Статус" size="small" style={{ marginBottom: 14 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Активен">
                  {client.isActive ? (
                    <Tag color="green">Да</Tag>
                  ) : (
                    <Tag color="red">Нет</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Включён">
                  {client.isEnabled ? (
                    <Tag color="green">Да</Tag>
                  ) : (
                    <Tag color="red">Нет</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Приостановлен">
                  {client.isSuspended ? (
                    <Tag color="orange">Да</Tag>
                  ) : (
                    <Tag color="green">Нет</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Email-уведомления">
                  {client.notifyByEmail ? 'Да' : 'Нет'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          ) : (
            <Card title="Тип оплаты" size="small" style={{ marginBottom: 14 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Тип">
                  <Tag color="default">Постоплата</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Email-уведомления">
                  {client.notifyByEmail ? 'Да' : 'Нет'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {client.plan && (
            <Card title="Тариф" size="small" style={{ marginBottom: 14 }}>
              <Text strong style={{ fontSize: 13 }}>{client.plan.name}</Text>
            </Card>
          )}

          {client.serviceType && (
            <Card title="Тип услуги" size="small" style={{ marginBottom: 14 }}>
              <Text style={{ fontSize: 13 }}>{client.serviceType.name}</Text>
            </Card>
          )}
        </Col>
      </Row>

      {client.contragent && (
        <Card
          title={
            <Space>
              <BankOutlined />
              Контрагент
            </Space>
          }
          style={{ marginTop: 20 }}
          size="small"
        >
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
            <Descriptions.Item label="Название">{client.contragent.name}</Descriptions.Item>
            <Descriptions.Item label="Полное название" span={2}>
              {client.contragent.fullName}
            </Descriptions.Item>
            <Descriptions.Item label="БИН">{client.contragent.bin}</Descriptions.Item>
            <Descriptions.Item label="Телефон">{client.contragent.phone}</Descriptions.Item>
            <Descriptions.Item label="Email">{client.contragent.email}</Descriptions.Item>
            {client.contragent.contactPerson && (
              <Descriptions.Item label="Контактное лицо">
                {client.contragent.contactPerson}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}
    </div>
  );
}
